'use client';

import { MapPin, Phone } from 'lucide-react';
import dynamic from 'next/dynamic';
import ContactModal from '@/dealership/ContactModal';
import DealershipFamily from '~/constants/dealership/DealershipFamily';
import dealershipFamilyMap from '~/constants/dealership/dealershipFamilyMap';
import { appAssetsCdnUrl } from '~/constants/environment';
import { SectionAnalyticsProvider } from '~/context/common/SectionAnalyticsContext';
import { useModalContext } from '~/context/dealership/ModalContext';
import { useDealershipId } from '~/context/dealership/StaticContext';
import Links from './Links';
import { Container, NavButton } from './PageHeader.styles';

const LogosLoading = () => <div className="w-48" />;

const brandLogosMap: Record<DealershipFamily, React.ComponentType> = {
  [DealershipFamily.Cdjr]: dynamic(
    () =>
      import(
        /* webpackChunkName: "CdjrBrandLogos" */ '@/dealership/family/cdjr/BrandLogos'
      ),
    { loading: LogosLoading, ssr: false }
  )
};

export default function PageHeader() {
  const section = 'Page Header';
  const dealershipId = useDealershipId();
  const { setContactModalOpen } = useModalContext();

  const dealershipFamily = dealershipId
    ? dealershipFamilyMap[dealershipId]
    : null;

  const BrandLogos = dealershipFamily
    ? brandLogosMap[dealershipFamily] ?? LogosLoading
    : LogosLoading;

  return (
    <>
      <SectionAnalyticsProvider section={section}>
        <header
          data-testid="dealership-page-header"
          className="flex-row border-b border-grey-2 bg-white px-2xl sm:px-4xl"
        >
          <Container>
            <div className="flex h-3xl flex-row items-center justify-between sm:justify-center sm:gap-2xl">
              <div className="flex items-center">
                <a
                  data-adobe-id="home"
                  data-analytics-id={section}
                  data-analytics-attributes={JSON.stringify({
                    item: 'Dealership logo',
                    section
                  })}
                  href="/"
                  data-testid="dealership-logo"
                >
                  <img
                    alt="Dealership logo"
                    className="h-3xl object-cover"
                    src={`${appAssetsCdnUrl}/images/dealership/${dealershipFamily}/logo.svg`}
                  />
                  <p className="sr-only">Home</p>
                </a>
                <div className="p-lg md:p-xl">
                  <span className="w-[1px] border border-border-subtle" />
                </div>
                <BrandLogos />
              </div>

              <Links />
            </div>

            <div className="flex flex-row items-center gap-3xl">
              <div className="flex flex-row items-center gap-md">
                <NavButton
                  data-adobe-id="open-contact-information"
                  data-analytics-id={section}
                  data-analytics-attributes={JSON.stringify({
                    item: 'Phone Icon',
                    section
                  })}
                  data-testid="phone-icon"
                  className="flex items-center text-text-strong"
                  onClick={() => setContactModalOpen(true)}
                >
                  <Phone size={24} />
                </NavButton>

                <NavButton
                  className="flex items-center text-text-strong"
                  data-adobe-id="open-directions"
                  data-analytics-id={section}
                  data-analytics-attributes={JSON.stringify({
                    item: 'Map Pin Icon',
                    section
                  })}
                  data-testid="map-pin-icon"
                  onClick={() => setContactModalOpen(true)}
                >
                  <MapPin size={24} />
                </NavButton>
              </div>
            </div>
          </Container>

          <Links isMobile={true} />
        </header>
      </SectionAnalyticsProvider>

      <ContactModal />
    </>
  );
}
